import React, { useState, useEffect } from 'react';
import { Timer, Plus, Edit, Trash2, AlertTriangle, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Select, Textarea } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog, Dialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { productionService } from '@services/productionService';
import {
  ProductionActivity,
  ProductionEmployee,
  ProductionOrder,
  ProductionActivityLog,
  ProductionActivityLogCreateData,
  PRODUCTION_ACTIVITY_CATEGORIES,
} from '@/types/production';

/** Data de hoje em AAAA-MM-DD (fuso local, sem shift) */
const todayISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

/** Formata data ISO para dd/mm/aaaa sem deslocamento de fuso (parse manual) */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return '-';
};

/** Extrai AAAA-MM-DD de uma data ISO (com ou sem hora) */
const toDateOnly = (value?: string): string => {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : '';
};

const parseIntOrZero = (value: string): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

interface EntryFormState {
  log_date: string;
  employee_id: string;
  activity_id: string;
  production_order_id: string;
  quantity_ok: string;
  quantity_defect: string;
  time_spent_minutes: string;
  notes: string;
}

const emptyEntry = (keepDate?: string, keepEmployee?: string): EntryFormState => ({
  log_date: keepDate || todayISO(),
  employee_id: keepEmployee || '',
  activity_id: '',
  production_order_id: '',
  quantity_ok: '',
  quantity_defect: '',
  time_spent_minutes: '',
  notes: '',
});

/** Select de atividades agrupado por categoria */
const ActivityOptions: React.FC<{ activities: ProductionActivity[] }> = ({ activities }) => (
  <>
    <option value="">Selecione a atividade...</option>
    {PRODUCTION_ACTIVITY_CATEGORIES.map((category) => {
      const group = activities.filter((a) => a.category === category.value);
      if (group.length === 0) return null;
      return (
        <optgroup key={category.value} label={category.label}>
          {group.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {activity.code} - {activity.name}
            </option>
          ))}
        </optgroup>
      );
    })}
  </>
);

/**
 * Apontamentos de Produção — entrada rápida + listagem com filtros
 */
export const ActivityLogListView: React.FC = () => {
  // Dados de apoio (selects)
  const [activities, setActivities] = useState<ProductionActivity[]>([]);
  const [employees, setEmployees] = useState<ProductionEmployee[]>([]);
  const [openOrders, setOpenOrders] = useState<ProductionOrder[]>([]);

  // Entrada rápida
  const [entry, setEntry] = useState<EntryFormState>(emptyEntry());
  const [entryError, setEntryError] = useState<string | null>(null);
  const [entrySuccess, setEntrySuccess] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);

  // Listagem
  const [items, setItems] = useState<ProductionActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterActivity, setFilterActivity] = useState('');
  const [filterOrder, setFilterOrder] = useState('');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Edição (modal)
  const [editTarget, setEditTarget] = useState<ProductionActivityLog | null>(null);
  const [editForm, setEditForm] = useState<EntryFormState>(emptyEntry());
  const [editError, setEditError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Exclusão
  const [deleteTarget, setDeleteTarget] = useState<ProductionActivityLog | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ============================================================
  // Carregamentos
  // ============================================================
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [activitiesResult, employeesResult, ordersResult] = await Promise.all([
          productionService.listActivities({ page: 1, pageSize: 100, is_active: true }),
          productionService.listEmployees({ page: 1, pageSize: 100, is_active: true }),
          productionService.listProductionOrders({ page: 1, pageSize: 100 }),
        ]);
        setActivities(activitiesResult.data);
        setEmployees(employeesResult.data);
        // Apenas OPs Planejadas (0) ou Em Produção (1)
        setOpenOrders(ordersResult.data.filter((o) => o.status === 0 || o.status === 1));
      } catch {
        // Falha silenciosa nos selects — a listagem exibirá erros se houver
      }
    };
    loadOptions();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await productionService.listActivityLogs({
        page: currentPage,
        pageSize,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
        employee_id: filterEmployee ? parseInt(filterEmployee) : undefined,
        activity_id: filterActivity ? parseInt(filterActivity) : undefined,
        production_order_id: filterOrder ? parseInt(filterOrder) : undefined,
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar apontamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterDateFrom, filterDateTo, filterEmployee, filterActivity, filterOrder]);

  // ============================================================
  // Entrada rápida
  // ============================================================
  const validateEntry = (form: EntryFormState): string | null => {
    if (!form.log_date) return 'Informe a data do apontamento';
    if (!form.employee_id) return 'Selecione o colaborador';
    if (!form.activity_id) return 'Selecione a atividade';

    const ok = parseIntOrZero(form.quantity_ok);
    const defect = parseIntOrZero(form.quantity_defect);
    if (ok < 0 || defect < 0) return 'As quantidades não podem ser negativas';
    if (ok + defect <= 0) return 'Informe ao menos uma quantidade (OK ou defeito) maior que zero';

    if (form.time_spent_minutes.trim() !== '') {
      const time = parseIntOrZero(form.time_spent_minutes);
      if (time <= 0) return 'Tempo (minutos) deve ser maior que zero';
    }

    return null;
  };

  const buildPayload = (form: EntryFormState): ProductionActivityLogCreateData => ({
    activity_id: parseInt(form.activity_id),
    employee_id: parseInt(form.employee_id),
    production_order_id: form.production_order_id ? parseInt(form.production_order_id) : undefined,
    log_date: form.log_date,
    quantity_ok: parseIntOrZero(form.quantity_ok),
    quantity_defect: parseIntOrZero(form.quantity_defect),
    time_spent_minutes:
      form.time_spent_minutes.trim() !== '' ? parseIntOrZero(form.time_spent_minutes) : undefined,
    notes: form.notes.trim() || undefined,
  });

  const handleRegister = async () => {
    setEntrySuccess(null);
    const validationError = validateEntry(entry);
    if (validationError) {
      setEntryError(validationError);
      return;
    }

    try {
      setRegistering(true);
      setEntryError(null);
      await productionService.createActivityLog(buildPayload(entry));
      // Mantém data e colaborador para lançamentos em sequência
      setEntry(emptyEntry(entry.log_date, entry.employee_id));
      setEntrySuccess('Apontamento registrado com sucesso');
      loadItems();
    } catch (err) {
      setEntryError(err instanceof Error ? err.message : 'Erro ao registrar apontamento');
    } finally {
      setRegistering(false);
    }
  };

  // ============================================================
  // Edição
  // ============================================================
  const openEdit = (log: ProductionActivityLog) => {
    setEditError(null);
    setEditTarget(log);
    setEditForm({
      log_date: toDateOnly(log.log_date),
      employee_id: String(log.employee_id),
      activity_id: String(log.activity_id),
      production_order_id: log.production_order_id ? String(log.production_order_id) : '',
      quantity_ok: String(log.quantity_ok ?? 0),
      quantity_defect: String(log.quantity_defect ?? 0),
      time_spent_minutes:
        log.time_spent_minutes !== undefined && log.time_spent_minutes !== null
          ? String(log.time_spent_minutes)
          : '',
      notes: log.notes || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const validationError = validateEntry(editForm);
    if (validationError) {
      setEditError(validationError);
      return;
    }

    try {
      setSavingEdit(true);
      setEditError(null);
      await productionService.updateActivityLog(editTarget.id, buildPayload(editForm));
      setEditTarget(null);
      loadItems();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Erro ao atualizar apontamento');
    } finally {
      setSavingEdit(false);
    }
  };

  // ============================================================
  // Exclusão
  // ============================================================
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await productionService.deleteActivityLog(deleteTarget.id);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir apontamento');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // OP do apontamento em edição pode estar concluída/cancelada — inclui no select se necessário
  const editOrderOptions =
    editTarget?.production_order_id &&
    !openOrders.some((o) => o.id === editTarget.production_order_id)
      ? [
          ...openOrders,
          {
            id: editTarget.production_order_id,
            code: editTarget.production_order_code || `OP #${editTarget.production_order_id}`,
          } as ProductionOrder,
        ]
      : openOrders;

  const renderEditFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-label block mb-1">Data *</label>
          <DateInputBR
            className="input w-full"
            value={editForm.log_date}
            onChange={(v) => setEditForm((prev) => ({ ...prev, log_date: v }))}
          />
        </div>
        <Select
          label="Colaborador *"
          value={editForm.employee_id}
          onChange={(e) => setEditForm((prev) => ({ ...prev, employee_id: e.target.value }))}
        >
          <option value="">Selecione...</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.name}
            </option>
          ))}
        </Select>
        <Select
          label="Atividade *"
          value={editForm.activity_id}
          onChange={(e) => setEditForm((prev) => ({ ...prev, activity_id: e.target.value }))}
        >
          <ActivityOptions activities={activities} />
        </Select>
        <Select
          label="Ordem de Produção"
          value={editForm.production_order_id}
          onChange={(e) =>
            setEditForm((prev) => ({ ...prev, production_order_id: e.target.value }))
          }
        >
          <option value="">Sem OP</option>
          {editOrderOptions.map((order) => (
            <option key={order.id} value={order.id}>
              {order.code}
            </option>
          ))}
        </Select>
        <div>
          <label className="text-label block mb-1">Qtde OK *</label>
          <input
            type="number"
            className="input w-full"
            min={0}
            value={editForm.quantity_ok}
            onChange={(e) => setEditForm((prev) => ({ ...prev, quantity_ok: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-label block mb-1">Qtde Defeito</label>
          <input
            type="number"
            className="input w-full"
            min={0}
            value={editForm.quantity_defect}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, quantity_defect: e.target.value }))
            }
          />
        </div>
        <div>
          <label className="text-label block mb-1">Tempo (min)</label>
          <input
            type="number"
            className="input w-full"
            min={1}
            value={editForm.time_spent_minutes}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, time_spent_minutes: e.target.value }))
            }
          />
        </div>
      </div>
      <Textarea
        label="Observação"
        rows={2}
        value={editForm.notes}
        onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
      />
      {editError && <p className="text-sm text-red-600">{editError}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-h1">Apontamentos de Produção</h1>
        <p className="text-slate-600">
          Registre a produção diária dos colaboradores por atividade
        </p>
      </div>

      {/* Entrada rápida */}
      <PermissionGuard permission="production.create">
        <Card>
          <CardHeader
            title="Novo Apontamento"
            subtitle="A data e o colaborador são mantidos para lançamentos em sequência"
          />
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-label block mb-1">Data *</label>
                <DateInputBR
                  className="input w-full"
                  value={entry.log_date}
                  onChange={(v) => setEntry((prev) => ({ ...prev, log_date: v }))}
                />
              </div>
              <Select
                label="Colaborador *"
                value={entry.employee_id}
                onChange={(e) => setEntry((prev) => ({ ...prev, employee_id: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Atividade *"
                value={entry.activity_id}
                onChange={(e) => setEntry((prev) => ({ ...prev, activity_id: e.target.value }))}
              >
                <ActivityOptions activities={activities} />
              </Select>
              <Select
                label="Ordem de Produção"
                value={entry.production_order_id}
                onChange={(e) =>
                  setEntry((prev) => ({ ...prev, production_order_id: e.target.value }))
                }
              >
                <option value="">Sem OP</option>
                {openOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.code}
                  </option>
                ))}
              </Select>
              <div>
                <label className="text-label block mb-1">Qtde OK *</label>
                <input
                  type="number"
                  className="input w-full"
                  placeholder="0"
                  min={0}
                  value={entry.quantity_ok}
                  onChange={(e) => setEntry((prev) => ({ ...prev, quantity_ok: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-label block mb-1">Qtde Defeito</label>
                <input
                  type="number"
                  className="input w-full"
                  placeholder="0"
                  min={0}
                  value={entry.quantity_defect}
                  onChange={(e) =>
                    setEntry((prev) => ({ ...prev, quantity_defect: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-label block mb-1">Tempo (min)</label>
                <input
                  type="number"
                  className="input w-full"
                  placeholder="Opcional"
                  min={1}
                  value={entry.time_spent_minutes}
                  onChange={(e) =>
                    setEntry((prev) => ({ ...prev, time_spent_minutes: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-label block mb-1">Observação</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="Opcional"
                  value={entry.notes}
                  onChange={(e) => setEntry((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
              <div>
                {entryError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle size={14} /> {entryError}
                  </p>
                )}
                {entrySuccess && !entryError && (
                  <p className="text-sm text-emerald-600">{entrySuccess}</p>
                )}
              </div>
              <Button onClick={handleRegister} loading={registering} icon={<Plus size={18} />}>
                Registrar
              </Button>
            </div>
          </CardContent>
        </Card>
      </PermissionGuard>

      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-label block mb-1">Data inicial</label>
              <DateInputBR
                className="input w-full"
                value={filterDateFrom}
                onChange={(v) => {
                  setCurrentPage(1);
                  setFilterDateFrom(v);
                }}
              />
            </div>
            <div>
              <label className="text-label block mb-1">Data final</label>
              <DateInputBR
                className="input w-full"
                value={filterDateTo}
                onChange={(v) => {
                  setCurrentPage(1);
                  setFilterDateTo(v);
                }}
              />
            </div>
            <Select
              label="Colaborador"
              value={filterEmployee}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterEmployee(e.target.value);
              }}
            >
              <option value="">Todos</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </Select>
            <Select
              label="Atividade"
              value={filterActivity}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterActivity(e.target.value);
              }}
            >
              <option value="">Todas</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.code} - {activity.name}
                </option>
              ))}
            </Select>
            <Select
              label="Ordem de Produção"
              value={filterOrder}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterOrder(e.target.value);
              }}
            >
              <option value="">Todas</option>
              {openOrders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.code}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando apontamentos...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <Timer className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum apontamento encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Data</th>
                  <th className="text-left">Colaborador</th>
                  <th className="text-left">Atividade</th>
                  <th className="text-left">OP</th>
                  <th className="text-right">OK</th>
                  <th className="text-right">Defeitos</th>
                  <th className="text-right">Tempo (min)</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <span className="text-sm text-slate-700">{formatDate(log.log_date)}</span>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{log.employee_name || '-'}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {log.activity_code && (
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                            {log.activity_code}
                          </code>
                        )}
                        <span className="text-sm text-slate-700">{log.activity_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.production_order_code ? (
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                          {log.production_order_code}
                        </code>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-emerald-700">
                        {(log.quantity_ok ?? 0).toLocaleString('pt-BR')}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {log.quantity_defect ? (
                        <span className="font-medium text-red-600">
                          {log.quantity_defect.toLocaleString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {log.time_spent_minutes ? (
                        <span className="text-sm text-slate-700">
                          {log.time_spent_minutes.toLocaleString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="production.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEdit(log)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="production.delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(log)}
                            icon={<Trash2 size={14} />}
                          >
                            Excluir
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Modal de edição */}
      <Dialog
        isOpen={editTarget !== null}
        onClose={() => !savingEdit && setEditTarget(null)}
        title="Editar Apontamento"
        size="lg"
        closeOnBackdrop={!savingEdit}
        showCloseButton={!savingEdit}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={savingEdit}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} loading={savingEdit} icon={<Save size={18} />}>
              Salvar
            </Button>
          </div>
        }
      >
        {editTarget && renderEditFields()}
      </Dialog>

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Apontamento"
        message={`Tem certeza que deseja excluir o apontamento de "${
          deleteTarget?.employee_name || 'colaborador'
        }" em ${formatDate(deleteTarget?.log_date)}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
